import type { PaymentMethod } from '@/wabunka-src/constants/paymentMethod';
import { useFincode, usePayjp, type PaymentCardTokenResponse } from '@/wabunka-src/features/Experience';

interface PaymentClient {
  loadPaymentForm: (waitForScriptLoad?: boolean) => Promise<void>;
  unLoadPaymentForm: () => void;
  createToken: () => Promise<PaymentCardTokenResponse>;
  isBelowMinimumPaymentPrice: (price: number) => boolean;
}

/**
 * 決済方法に応じた決済クライアントを返す。
 *
 * `paymentMethod` はリアクティブ（ref / getter）も受け付け、各メソッドの**呼び出し時**に PSP を解決する。
 * これにより、予約種別が確定してから paymentMethod が変わるケース（B-1）でも、
 * カードフォーム読み込み・トークン化・最低金額判定が常に最新の PSP で行われる。
 * プレーンな値を渡した場合は従来どおりその値で固定される（`toValue` で互換）。
 */
export const usePaymentClient = (paymentMethod: MaybeRefOrGetter<PaymentMethod>): PaymentClient => {
  const { loadPayjpPaymentForm, unLoadPayjpPaymentForm, createPayjpToken, isBelowPayjpMinimumPaymentPrice } = usePayjp();
  const { loadFincodePaymentForm, unLoadFincodePaymentForm, createFincodeToken, isBelowFincodeMinimumPaymentPrice } = useFincode();

  const isPayjp = () => toValue(paymentMethod) === 'payjp';

  return {
    // 現状loadPayjpPaymentFormのレスポンスを使用していないため、voidを返す
    loadPaymentForm: async (waitForScriptLoad?: boolean) => {
      if (isPayjp()) {
        await loadPayjpPaymentForm(waitForScriptLoad);
        return;
      }
      await loadFincodePaymentForm();
    },
    unLoadPaymentForm: () => {
      if (isPayjp()) {
        unLoadPayjpPaymentForm();
        return;
      }
      // 入力 DOM は呼び出し側で破棄される。ここでは破棄済みフォームを指す ui / instance を捨てる
      unLoadFincodePaymentForm();
    },
    createToken: () => (isPayjp() ? createPayjpToken() : createFincodeToken()),
    isBelowMinimumPaymentPrice: (price: number) => (isPayjp() ? isBelowPayjpMinimumPaymentPrice(price) : isBelowFincodeMinimumPaymentPrice(price)),
  };
};
